<template>
  <v-expansion-panel class="mb-4 message-dispatch-panel" :class="{ 'message-dispatch-panel--error': hasError }">
    <v-expansion-panel-header :disabled="disablePanel" @click.stop="togglePanel">
      <component
        :is="selectedDispatchModel"
        :settings="settings"
        :is-addition-disabled="isAdditionDisabled || isMariaCampaignFinished"
        :is-deletion-disabled="isMariaCampaignFinished"
        :disabled="isMariaCampaignFinished"
        @update-header="updateHeader"
        @duplicate="$emit('duplicate')"
        @delete="$emit('delete')"
      />
      <template v-slot:actions>
        <v-icon v-if="hasError" color="error" class="mr-2"> mdi-alert-circle </v-icon>
        <mf-icon :key="panelIconKey" :disabled="disablePanel" :color="disablePanel ? '#6b7580' : '#00366d'"> {{ panelIcon }} </mf-icon>
      </template>
    </v-expansion-panel-header>
    <v-expansion-panel-content>
      <div v-if="hasError" class="error--text">
        <ul class="error-messages">
          <li v-for="message in errorMessages" :key="message">{{ message }}</li>
        </ul>
      </div>
      <div>
        <v-row>
          <component
            :is="cardIsFilled(channel)"
            v-for="channel in sortedChannelKeys(settings.channels)"
            :key="cardKey(channel)"
            :settings-id="settings.id"
            :is-manual-priority="selectedChannelsType === 'USER'"
            :channel="settings.channels[channel]"
            :channels="settings.channels"
            :is-channel-active="isChannelActive(cardKey(channel))"
            :is-maria-campaign-active="isMariaCampaignActive"
            :disabled="isMariaCampaignFinished"
            @update-channel="updateChannel"
          />
        </v-row>
        <v-divider class="my-4"></v-divider>
        <h2 class="title my-4">Prioridade de canal</h2>
        <v-btn-toggle v-model="selectedChannelsType" class="btn-toggle" mandatory>
          <v-btn :class="{ active: selectedChannelsType === 'MARIA' }" :disabled="isMariaCampaignFinished" value="MARIA">
            Mar.ia escolhe o melhor canal para o cliente
          </v-btn>
          <v-btn :class="{ active: selectedChannelsType === 'USER' }" :disabled="isMariaCampaignFinished" value="USER">
            Usuário define a prioridade dos canais
          </v-btn>
          <v-btn :class="{ active: selectedChannelsType === 'ALL_AVAILABLE_CHANNELS' }" :disabled="isMariaCampaignFinished" value="ALL_AVAILABLE_CHANNELS">
            Disparo é realizado em todos os canais
          </v-btn>
        </v-btn-toggle>
        <p class="description mt-4" v-html="channelsTypeDescriptions[selectedChannelsType]"></p>
      </div>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import ChannelCard from './ChannelCard.vue'
import EmptyChannelCard from './EmptyChannelCard.vue'
import MariaHeaderDispatchPanel from './MessageDispatchPanelHeader/MariaHeaderDispatchPanel.vue'
import RecurrentHeaderDispatchPanel from './MessageDispatchPanelHeader/RecurrentHeaderDispatchPanel.vue'
import StaticDateHeaderDispatchPanel from './MessageDispatchPanelHeader/StaticDateHeaderDispatchPanel.vue'
import { mariaCampaign } from '@/modules/maria/campanha-da-maria-v2/mixins/maria-campaign'

export default {
  name: 'MessageDispatchPanel',
  components: {
    ChannelCard,
    MariaHeaderDispatchPanel,
    RecurrentHeaderDispatchPanel,
    StaticDateHeaderDispatchPanel,
    EmptyChannelCard,
  },
  mixins: [mariaCampaign],
  emits: ['update-settings', 'duplicate', 'delete'],
  props: {
    settings: {
      type: Object,
      required: true,
    },
    isAdditionDisabled: {
      type: Boolean,
      required: true,
      default: false,
    },
    activePanel: {
      type: Number,
    },
    panelIndex: {
      type: Number,
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    errorMessages: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      channelsTypeDescriptions: {
        MARIA:
          'Nesse modelo, a <strong>Mar.ia</strong> seleciona automaticamente o canal mais eficaz para cada consumidor, utilizando dados e comportamentos de interação. Não é necessário configurar manualmente nenhuma prioridade.',
        USER: 'Nesse modelo você pode selecionar a prioridade de cada canal para personalizar o envio de mensagens conforme sua estratégia. O canal com prioridade 1 é o de maior prioridade.',
        ALL_AVAILABLE_CHANNELS:
          'A máxima cobertura enviando mensagens para todos os canais disponíveis do consumidor. Nesse modelo, um mesmo consumidor poderá receber a mesma mensagem em vários canais simultaneamente.',
      },
    }
  },
  computed: {
    selectedChannelsType: {
      set(value) {
        this.$emit('update-settings', { ...this.settings, selector_channels_type: value })
      },
      get() {
        return this.settings.selector_channels_type
      },
    },
    selectedDispatchModel() {
      const selectedDispatchModel = this.campaign?.messages_dispatcher?.settings?.model || 'MARIA'
      return {
        MARIA: 'maria-header-dispatch-panel',
        RECURRENT: 'recurrent-header-dispatch-panel',
        STATIC_DATE: 'static-date-header-dispatch-panel',
      }[selectedDispatchModel]
    },
    mapChannelsNames() {
      return {
        SMS: 'sms',
        'Push App': 'push',
        'E-mail': 'email',
        WhatsApp: 'whatsapp',
      }
    },
    realPanelIndex() {
      return this.campaign.messages_dispatcher.settings.schedule_settings.findIndex((setting) => setting.id === this.settings.id)
    },
    disablePanel() {
      const totalPanels = this.campaign.messages_dispatcher.settings.schedule_settings.length
      return totalPanels === 1 && this.realPanelIndex === 0
    },
    panelIcon() {
      return this.activePanel === this.realPanelIndex ? 'keyboard_arrow_up' : 'keyboard_arrow_down'
    },
    panelIconKey() {
      return `${this.realPanelIndex}-${this.activePanel === this.realPanelIndex ? 'open' : 'closed'}`
    },
  },
  mounted() {
    this.updateChannel()
  },
  methods: {
    cardIsFilled(channel) {
      let card = 'empty-channel-card'
      if (channel === 'email') {
        const message = this.settings?.channels?.[channel]?.message
        if (message?.email_configuration_type === 'BUILDER') {
          card = Object.keys(message?.builder_email_configuration || {}).length > 0 ? 'channel-card' : 'empty-channel-card'
        } else if (message?.email_configuration_type === 'SIMPLE') {
          card = message?.simple_email_configuration?.text ? 'channel-card' : 'empty-channel-card'
        } else if (message?.email_configuration_type === 'HTML') {
          card = message?.html_email_configuration?.text ? 'channel-card' : 'empty-channel-card'
        }
        return card
      }
      if (channel === 'push') {
        card = this.settings?.channels?.[channel]?.message?.text || this.settings?.channels?.[channel]?.message?.title ? 'channel-card' : 'empty-channel-card'
        return card
      }
      card = this.settings?.channels?.[channel]?.message?.text ? 'channel-card' : 'empty-channel-card'
      return card
    },
    cardKey(channel) {
      let name = this.settings?.channels?.[channel]?.name ?? ''
      return name
    },
    sortedChannelKeys(channels) {
      return Object.entries(channels)
        .sort(([, a], [, b]) => {
          const getCategory = (channel) => {
            const hasMessage = this.hasChannelMessage(channel)
            if (channel.active && hasMessage) return 0
            if (!channel.active && hasMessage) return 1
            return 2
          }

          const categoryA = getCategory(a)
          const categoryB = getCategory(b)

          if (categoryA !== categoryB) return categoryA - categoryB

          return (a.priority || Infinity) - (b.priority || Infinity)
        })
        .map(([key]) => key)
    },
    isChannelActive(channelName) {
      return this.getChannelsPermissions[this.mapChannelsNames[channelName]]
    },
    updateChannel(updatedChannel = null) {
      const channelsArray = Object.values(this.settings.channels).map((channel) => (channel.name === updatedChannel?.name ? updatedChannel : channel))

      const sortedChannels = channelsArray.sort((a, b) => {
        const getCategory = (ch) => {
          if (ch.active && this.hasChannelMessage(ch)) return 0
          if (!ch.active && this.hasChannelMessage(ch)) return 1
          return 2
        }

        const categoryA = getCategory(a)
        const categoryB = getCategory(b)

        if (categoryA !== categoryB) return categoryA - categoryB

        return (a.priority || Infinity) - (b.priority || Infinity)
      })

      const updatedChannels = sortedChannels.map((channel, index) => {
        const isActiveWithMessage = channel.active && this.hasChannelMessage(channel)
        const isInactiveWithMessage = !channel.active && this.hasChannelMessage(channel)

        let priority = ''

        if (isActiveWithMessage) {
          priority = index + 1
        } else if (isInactiveWithMessage) {
          priority = index + 1
        }

        return {
          ...channel,
          priority,
        }
      })

      const newChannels = updatedChannels.reduce((acc, channel) => {
        acc[this.mapChannelsNames[channel.name]] = channel
        return acc
      }, {})

      this.$emit('update-settings', {
        ...this.settings,
        channels: newChannels,
      })
    },
    updateStaticDate(send_date) {
      this.$emit('update-settings', { ...this.settings, send_date })
    },
    updateRecurrentDate(week_day) {
      this.$emit('update-settings', { ...this.settings, week_day })
    },
    updateHourSettings(hour) {
      this.$emit('update-settings', { ...this.settings, ...hour })
    },
    updateHeader({ updateDispatchModel, value }) {
      this[updateDispatchModel](value)
    },
    togglePanel() {
      this.$emit('update-active-panel', this.realPanelIndex)
    },
    hasChannelMessage(channel) {
      if (channel.name === 'E-mail') {
        const message = channel.message
        if (message?.email_configuration_type === 'BUILDER') {
          return Object.keys(message?.builder_email_configuration || {}).length > 0
        } else if (message?.email_configuration_type === 'SIMPLE') {
          return !!message?.simple_email_configuration?.text
        } else if (message?.email_configuration_type === 'HTML') {
          return !!message?.html_email_configuration?.text
        }
        return false
      }
      if (channel.name === 'Push App') {
        return !!(channel.message?.text || channel.message?.title)
      }
      return !!channel.message?.text
    },
  },
}
</script>

<style lang="scss" scoped>
.message-dispatch-panel {
  border-radius: 8px;
  box-shadow: none !important;
  -webkit-box-shadow: none !important;

  &--error {
    border: 1px solid #ff5252;
  }
}

/* Layout principal */
.btn-toggle {
  display: flex;
  border-radius: 8px;
  overflow: hidden;
  height: 40px; /* Ajuste baseado no protótipo */
  width: 75%;
  min-width: max-content !important;

  .v-btn {
    min-width: max-content !important;
    border: 1px solid #c4c4c4 !important;
  }

  .v-btn:last-of-type {
    border-left: none !important;
  }

  .v-btn:first-of-type {
    border-right: none !important;
  }
}

/* Botões */
.v-btn {
  flex: 1;
  font-size: 14px;
  font-weight: 400;
  text-transform: none; /* Para manter o texto fiel ao protótipo */
  border: none !important;
  height: 40px !important;
  letter-spacing: 0.4000000059604645px;
}
.theme--light.v-btn:not(.v-btn--flat):not(.v-btn--text):not(.v-btn--outlined) {
  background-color: #f6f6f6;
}
.v-btn:last-child {
  border-right: none;
}
.v-btn--active {
  background-color: #00366d !important; /* Azul mais escuro do protótipo */
  border: 1px solid #00366d !important;
  color: #ffffff !important;
}

.v-btn--active:before {
  opacity: 0 !important;
}

/* Texto superior */
.title {
  font-size: 16px !important;
  font-weight: 600;
  color: $cinza-d20;
  font-family: Lato !important;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
  letter-spacing: 0.4000000059604645px;
}

/* Descrição */
.description {
  font-size: 14px;
  font-weight: 400;
  color: #6b7580;
  line-height: 1.5;
  width: 60%;
  letter-spacing: 0.4000000059604645px;
}
.v-divider {
  height: 24px;
  background-color: rgba(0, 0, 0, 0.12);
}
</style>
